import React, {useEffect} from 'react';
import {Link} from 'react-router-dom';
import {CreditCardIcon} from './icons';
import {UPGRADE_URL} from '../support/upgradeUrl';
import {dismissUpgradePrompt, registerInlineUpgradeSurface} from '../support/upgradePrompt';

interface Props {
    /** Заголовок панели (из тела 403, поле {@code error}); fallback — дефолт. */
    title?: string;
    /** Пояснение с подсказкой апгрейда (поле {@code description}); fallback — дефолт. */
    description?: string;
    /** {@code data-testid} панели; CTA получает суффикс {@code -cta}. */
    testId?: string;
}

/**
 * Инлайн-панель premium-апселла (CTHS-74). Показывается виджетом аналитики вместо
 * load-error с бесполезным «Повторить», когда запрос упирается в premium-гейт (402/403):
 * понятное объяснение + кнопка перехода к тарифам. CTA ведёт на внутренний {@link UPGRADE_URL}
 * (а не на внешний portal напрямую), поэтому кнопка доступна даже без сконфигурированного
 * {@code upgradePortalUrl} — сама страница тарифов ведёт на портал апгрейда.
 *
 * Глобальный {@link UpgradePrompt}-тост (CTHS-21) остаётся: это его контекстное дополнение —
 * объяснение прямо на месте premium-виджета, чтобы FREE-пользователь не принял гейт за поломку.
 */
const PremiumUpsell: React.FC<Props> = ({title, description, testId = 'premium-upsell'}) => {
    /**
     * Панель объясняет гейт сама, поэтому пока она на экране, глобальный тост не нужен:
     * на один отказ пользователь получал два сообщения — всплывашку поверх колокольчика и
     * баннер в теле страницы (Q19). Уже показанный тост снимаем.
     */
    useEffect(() => {
        const release = registerInlineUpgradeSurface();
        dismissUpgradePrompt();
        return release;
    }, []);

    return (
    <section
        data-testid={testId}
        className="rounded-xl border border-brand-200 dark:border-brand-800 bg-brand-50/40 dark:bg-brand-900/20 p-5"
    >
        <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-xl bg-brand-100 dark:bg-brand-900/40
                            flex items-center justify-center text-brand-600 dark:text-brand-400">
                <CreditCardIcon className="w-5 h-5"/>
            </div>
            <div className="min-w-0 flex-1 space-y-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                    {title || 'Доступно в Premium'}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    {description || 'Эта аналитика доступна на тарифах PRO и FAMILY. Обновите тариф, чтобы открыть её.'}
                </p>
                <Link
                    to={UPGRADE_URL}
                    data-testid={`${testId}-cta`}
                    className="inline-flex min-h-[44px] items-center px-4 rounded-xl bg-brand-600 text-white
                               text-sm font-medium hover:bg-brand-700 transition-colors"
                >
                    Перейти к тарифам
                </Link>
            </div>
        </div>
    </section>
    );
};

export default PremiumUpsell;
